import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Icon,
  List,
  ListItem,
  Select,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useAllAppointmentListener } from "api/appointments";
import { format } from "date-fns";
import Link from "generics/Link";
import PageTitle from "generics/PageTitle";
import { FaWalking, FaHome } from "react-icons/fa";
import { SearchInput } from "generics/SearchInput";
import ToolbarPortal from "generics/ToolbarPortal";
import { useDebouncedCallback } from "hooks/useDebouncedCallback";
import React, { useState } from "react";

export default function AppointmentsPage() {
  const appointmentsQuery = useAllAppointmentListener({
    start: format(new Date().getTime(), "yyyy-MM-dd"),
  });

  const [filterTerm, setFilterTerm] = useState("");
  const debouncedOnChange = useDebouncedCallback(
    (e) => setFilterTerm(e.target.value),
    400
  );
  const [pickupStatus, setPickupStatus] = useState("all");

  if (appointmentsQuery.isLoading || !appointmentsQuery.data)
    return (
      <Box h="300px">
        <Center w="100%" h="100%">
          <Spinner size="lg" />
        </Center>
      </Box>
    );

  function getResult(patient: any) {
    if (patient.status === "completed")
      return <Badge colorScheme="green">Published</Badge>;
    const completedTests = Object.values(patient.tests).filter(
      (test: any) => test.status === "completed"
    );
    if (completedTests.length === Object.values(patient.tests).length)
      return <Badge colorScheme="green">Published</Badge>;
    if (completedTests.length === 0)
      return <Badge colorScheme="red">Not Published</Badge>;
    return (
      <Stack direction="row">
        <Badge colorScheme="green">{completedTests.length} Published</Badge>
        <Badge colorScheme="red">
          {Object.values(patient.tests).length - completedTests.length}{" "}
          Remaining
        </Badge>
      </Stack>
    );
  }

  let appointments =
    appointmentsQuery.data?.filter((appointment: any) =>
      appointment.user?.name.toLowerCase().includes(filterTerm.toLowerCase())
    ) || [];


  return (
    <Box px={12}>
      <ToolbarPortal>
        <Flex justifyContent="space-between" alignItems="center">
          <PageTitle flexGrow={1}>Appointments</PageTitle>
          <Stack direction="row" spacing={8}>
            <Link to="/appointments/all">
              <Button>View All</Button>
            </Link>
            <Select onChange={(e) => setPickupStatus(e.target.value)}>
              <option value="all">All Patients</option>
              <option value="lab">Walk In Patient</option>
              <option value="home">Sample Collected at Home</option>
            </Select>
            <SearchInput onChange={(e) => debouncedOnChange(e)} />
          </Stack>
        </Flex>
      </ToolbarPortal>
      <Table>
        <Thead>
          <Tr>
            <Th>USER</Th>
            <Th>STATUS</Th>
            <Th>DATE</Th>
            <Th w="15%">Patient Names</Th>
            <Th>Tests</Th>
            <Th>Results</Th>
          </Tr>
        </Thead>
        <Tbody>
          {appointments.length === 0 ? (
            <Tr>
              <Td colSpan={6}>
                <Text textAlign="center" color="gray.500">
                  No Appointments!
                </Text>
              </Td>
            </Tr>
          ) : (
            appointments.map((appointment: any) => {
              if (pickupStatus === "home" && appointment.submitSampleAtLab)
                return null;
              if (pickupStatus === "lab" && !appointment.submitSampleAtLab)
                return null;
              return (
                <Tr fontSize="sm" key={appointment.id}>
                  <Td>
                    <Flex>
                      <Link to={`/appointments/${appointment.id}`}>
                        {`${appointment.user.name}: #${appointment.user.readableId}`}
                      </Link>
                      <Box width={1}></Box>
                      <Icon
                        color="primary.500"
                        as={appointment.submitSampleAtLab ? FaWalking : FaHome}
                      ></Icon>
                    </Flex>
                  </Td>
                  <Td>
                    <Badge colorScheme="blue">
                      {appointment.status.split("_").join(" ")}
                    </Badge>
                  </Td>
                  <Td whiteSpace="nowrap">
                    {format(appointment.scheduledCollectionTime, "d MMM, yyyy")}
                  </Td>
                  <Td>
                    <List>
                      {Object.values(appointment.patients).map(
                        (patient: any) => (
                          <ListItem
                            key={patient.id}
                            py={1}
                            textTransform="capitalize"
                          >{`${patient.patientName}: #${patient.id}`}</ListItem>
                        )
                      )}
                    </List>
                  </Td>
                  <Td>
                    <List>
                      {Object.values(appointment.patients).map(
                        (patient: any) => (
                          <ListItem py={1} key={patient.id}>
                            {Object.values(patient.tests)
                              .map((test: any) => test.name)
                              .join(", ")}
                          </ListItem>
                        )
                      )}
                    </List>
                  </Td>
                  <Td>
                    <List>
                      {Object.values(appointment.patients).map(
                        (patient: any) => (
                          <ListItem key={patient.id} py={1}>
                            {getResult(patient)}
                          </ListItem>
                        )
                      )}
                    </List>
                  </Td>
                </Tr>
              );
            })
          )}
        </Tbody>
      </Table>
    </Box>
  );
}
